<template>
    <tr>
        <td>
            <span class="tag" :class="{
                'is-danger': leg.status === 'failed',
                'is-info': leg.status === 'running',
                'is-warning': leg.status === 'warning',
                'is-success': leg.status === 'success',
                'is-link': leg.status === 'syncing'
            }">{{ leg.status }}</span>
        </td>
        <td>
            <span v-if="leg.legStatus === 1" class="tag is-success">R</span>
            <span v-if="leg.legStatus === 2" class="tag is-warning">F</span>
        </td>
        <td>{{ leg.rules }}</td>
        <td>{{ formatDate(leg.departureDateTime) }}</td>
        <td>{{ leg.tailNumber }}</td>
        <td>{{ crewNames(leg.pilotLastName1, leg.pilotLastName2) }}</td>
        <td>
            <span class="tag is-black">
                {{ formatTime(leg.blockOutPlanned) }} {{ leg.departureAirport }}
            </span>
            <span class="tag is-black">
                {{ leg.arrivalAirport }} {{ formatTime(leg.blockInPlanned) }}
            </span>
        </td>
        <td>
            <leg-check :status="leg.checkTimes">
                <table class="table is-fullwidth">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Planned Date</th>
                            <th>Actual Date</th>
                            <th>Planned</th>
                            <th>Actual</th>
                            <th>Difference</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Out</td>
                            <td>{{ formatDateShort(leg.blockOutPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockOutActual}">{{ leg.blockOutActual ? formatDateShort(leg.blockOutActual) : '' }}</td>
                            <td>{{ formatTime(leg.blockOutPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockOutActual}">{{ leg.blockOutActual ? formatTime(leg.blockOutActual) : '' }}</td>
                            <td :class="{'is-warning': isDifferenceOutsideThreshold(leg.blockOutPlanned, leg.blockOutActual) && leg.blockOutActual}">{{ leg.blockOutActual ? (new Date(leg.blockOutActual) - new Date(leg.blockOutPlanned)) / 60000 : '' }}</td>
                        </tr>
                        <tr>
                            <td>Off</td>
                            <td>{{ formatDateShort(leg.blockOffPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockOffActual}">{{ leg.blockOffActual ? formatDateShort(leg.blockOffActual) : '' }}</td>
                            <td>{{ formatTime(leg.blockOffPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockOffActual}">{{ leg.blockOffActual ? formatTime(leg.blockOffActual) : '' }}</td>
                            <td :class="{'is-warning': isDifferenceOutsideThreshold(leg.blockOffPlanned, leg.blockOffActual) && leg.blockOffActual}">{{ leg.blockOffActual ? (new Date(leg.blockOffActual) - new Date(leg.blockOffPlanned)) / 60000 : '' }}</td>
                        </tr>
                        <tr>
                            <td>On</td>
                            <td>{{ formatDateShort(leg.blockOnPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockOnActual}">{{ leg.blockOnActual ? formatDateShort(leg.blockOnActual) : '' }}</td>
                            <td>{{ formatTime(leg.blockOnPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockOnActual}">{{ leg.blockOnActual ? formatTime(leg.blockOnActual) : '' }}</td>
                            <td :class="{'is-warning': isDifferenceOutsideThreshold(leg.blockOnPlanned, leg.blockOnActual) && leg.blockOnActual}">{{ leg.blockOnActual ? (new Date(leg.blockOnActual) - new Date(leg.blockOnPlanned)) / 60000 : '' }}</td>
                        </tr>
                        <tr>
                            <td>In</td>
                            <td>{{ formatDateShort(leg.blockInPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockInActual}">{{ leg.blockInActual ? formatDateShort(leg.blockInActual) : '' }}</td>
                            <td>{{ formatTime(leg.blockInPlanned) }}</td>
                            <td :class="{'is-danger': !leg.blockInActual}">{{ leg.blockInActual ? formatTime(leg.blockInActual) : '' }}</td>
                            <td :class="{'is-warning': isDifferenceOutsideThreshold(leg.blockInPlanned, leg.blockInActual) && leg.blockInActual}">{{ leg.blockInActual ? (new Date(leg.blockInActual) - new Date(leg.blockInPlanned)) / 60000 : '' }}</td>
                        </tr>
                    </tbody>
                </table>
                <span>Current Threshold: +/- {{ settings.blockTimeThreshold }}</span>
            </leg-check>
        </td>
        <td>
            <leg-check :status="leg.checkFMS">
                <template v-slot="{ isHovering }">
                    <div v-if="leg.fmsImageId" class="columns">
                        <div class="row"><div class="column">
                            <table class="table is-fullwidth">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Actual</th>
                                        <th>Detected</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr :class="{'is-danger': compareTimeStrings(leg.blockOutActual, leg.blockOutDetected) && leg.blockOutDetected}">
                                        <td>Out</td>
                                        <td>{{ formatTime(leg.blockOutActual) }}</td>
                                        <td>{{ formatTime(leg.blockOutDetected) }}</td>
                                    </tr>
                                    <tr :class="{'is-danger': compareTimeStrings(leg.blockOffActual, leg.blockOffDetected) && leg.blockOffDetected}">
                                        <td>Off</td>
                                        <td>{{ formatTime(leg.blockOffActual) }}</td>
                                        <td>{{ formatTime(leg.blockOffDetected) }}</td>
                                    </tr>
                                    <tr :class="{'is-danger': compareTimeStrings(leg.blockOnActual, leg.blockOnDetected) && leg.blockOnDetected}">
                                        <td>On</td>
                                        <td>{{ formatTime(leg.blockOnActual) }}</td>
                                        <td>{{ formatTime(leg.blockOnDetected) }}</td>
                                    </tr>
                                    <tr :class="{'is-danger': compareTimeStrings(leg.blockInActual, leg.blockInDetected) && leg.blockInDetected}">
                                        <td>In</td>
                                        <td>{{ formatTime(leg.blockInActual) }}</td>
                                        <td>{{ formatTime(leg.blockInDetected) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div></div>
                        <div class="row">
                            <div class="column" style="width: 300px;">
                                <!-- Old img src: :src="`https://rest.levelflight.com/api/image/${leg.fmsImageId}/thumb`" -->
                                <a :href="`https://rest.levelflight.com/api/image/${leg.fmsImageId}?bucket=levelflight-images`" target="_blank">
                                    <img 
                                        v-if="isHovering"
                                        :src="`https://rest.levelflight.com/api/image/${leg.fmsImageId}?bucket=levelflight-images`" 
                                        alt="FMS Loading..." 
                                        class="fms-preview"
                                    >
                                </a><br>
                                <span class="is-size-7">Click Image to Open</span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="has-text-centered" style="width: 200px;">
                        <span class="is-size-7">Image Missing from LevelFlight</span>
                    </div>
                </template>
            </leg-check>
        </td>
        <td>
            <leg-check :status="leg.checkFuel">
                <table class="table is-fullwidth">
                    <thead>
                        <tr>
                            <th>Start</th>
                            <th>Stop</th>
                            <th>Burn</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td :class="{'is-danger': leg.checkFuel === 'failed' && (!leg.fuelStart || leg.fuelStart === 0)}">{{ leg.fuelStart }}</td>
                            <td :class="{'is-danger': leg.checkFuel === 'failed' && (!leg.fuelStop || leg.fuelStop === 0)}">{{ leg.fuelStop }}</td>
                            <td :class="{'is-danger': leg.checkFuel === 'failed' && (!leg.fuelBurn || leg.fuelBurn === 0)}">{{ leg.fuelBurn }}</td>
                        </tr>
                    </tbody>
                </table>
                <span>Must have positive fuel burn</span>
            </leg-check>
        </td>
        <td>
            <leg-check :status="leg.checkPilot">
                <div style="width: 200px;">
                    <span class="has-text-weight-bold">Flew: </span>
                    <span>{{ leg.pilotFlying }}</span>
                </div>
            </leg-check>
        </td>
        <td>
            <leg-check :status="leg.checkWB">
                <table class="table is-fullwidth" style="width: 300px">
                    <tbody>
                        <tr>
                            <td class="has-text-weight-bold">Takeoff Weight</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Max Allowable</td>
                            <td :class="{'is-danger': !leg.wbTakeoffActual || leg.wbTakeoffMaxAllowable === 0}">{{ leg.wbTakeoffMaxAllowable }}</td>
                        </tr>
                        <tr>
                            <td>Actual</td>
                            <td :class="{'is-danger': !leg.wbTakeoffActual || leg.wbTakeoffActual === 0}">{{ leg.wbTakeoffActual }}</td>
                        </tr>
                        <tr>
                            <td class="has-text-weight-bold">Center of Gravity</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Forward</td>
                            <td :class="{'is-danger': !leg.wbCogForward || leg.wbCogForward === 0}">{{ leg.wbCogForward }}</td>
                        </tr>
                        <tr>
                            <td>Aft</td>
                            <td :class="{'is-danger': !leg.wbCogAft || leg.wbCogAft === 0}">{{ leg.wbCogAft }}</td>
                        </tr>
                        <tr>
                            <td>Actual</td>
                            <td :class="{'is-danger': !leg.wbCogActual || leg.wbCogActual === 0}">{{ leg.wbCogActual }}</td>
                        </tr>
                    </tbody>
                </table>
                <span>Must be completed</span>
            </leg-check>
        </td>
        <td class="has-text-right">
            <button v-if="!leg.deletedLocally" :class="{'button is-link mr-2': !isCompleting, 'button is-loading is-link mr-2': isCompleting}" @click="completeLeg(leg.$id)">Complete</button>
            <button class="button is-info" @click="openDispatchLink(leg.dispatchId)">
                Open
            </button>
            <button class="button is-text" @click="refreshLeg(leg.$id)">
                <font-awesome-icon 
                    :icon="['fas', 'rotate-right']" 
                    :class="{ 'fa-spin': leg.status === 'syncing' || isRefreshing }"
                />
            </button>
        </td>
    </tr>
</template>

<script>
import LegCheck from './LegCheck.vue';
import { useSettings } from '@/lib/settings';
import { useLegsStore } from '@/store/legsStore';
import { useSyncStore } from '@/store/syncStore';
import { ref } from 'vue';

export default {
    name: 'LegRow',
    setup() {
        const { settings } = useSettings();
        const legsStore = useLegsStore();
        const syncStore = useSyncStore();
        const isCompleting = ref(false);
        const isRefreshing = ref(false);
        const formatDate = (date) => {
            const dateObj = new Date(date);
            const day = dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
            const dayOfMonth = dateObj.toLocaleDateString('en-US', { day: '2-digit', timeZone: 'UTC' });
            const month = dateObj.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
            const year = dateObj.toLocaleDateString('en-US', { year: '2-digit', timeZone: 'UTC' });
            return `${day} ${dayOfMonth}${month}${year}`;
        };

        const formatTime = (time) => {
            return new Date(time).toLocaleTimeString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC'
            }).replace(/:/g, '');
        };

        const formatDateShort = (date) => {
            const dateObj = new Date(date);
            const dayOfMonth = dateObj.toLocaleDateString('en-US', { day: '2-digit', timeZone: 'UTC' });
            const month = dateObj.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
            return `${dayOfMonth}${month}`;
        };

        const isDifferenceOutsideThreshold = (planned, actual) => {
            if (!actual || !planned) return false;
            const difference = (new Date(actual) - new Date(planned)) / 60000;
            return Math.abs(difference) >= settings.blockTimeThreshold;
        };

        const openDispatchLink = (dispatchId) => {
            const url = `https://ops.levelflight.com/dispatches/${dispatchId}`;
            window.open(url, '_blank');
        };

        const completeLeg = async (legId) => {
            isCompleting.value = true;
            await legsStore.completeLeg(legId);
            isCompleting.value = false;
        };

        const refreshLeg = async (legId) => {
            isRefreshing.value = true;
            await syncStore.runSyncLeg(legId);
            isRefreshing.value = false;
        };

        const compareTimeStrings = (time1, time2) => {
            if (!time1 || !time2) return false;
            const t1 = new Date(time1).toLocaleTimeString('en-US', { 
                hour12: false, 
                hour: '2-digit', 
                minute: '2-digit', 
                timeZone: 'UTC' 
            });
            const t2 = new Date(time2).toLocaleTimeString('en-US', { 
                hour12: false, 
                hour: '2-digit', 
                minute: '2-digit', 
                timeZone: 'UTC' 
            });
            return t1 !== t2;
        };

        const crewNames = (lastName1, lastName2) => {
            return [lastName1, lastName2]
                .map(name => (name || '').replace(/\s+/g, ' ').trim())
                .filter(Boolean)
                .join(' / ');
        };

        return {
            settings,
            formatDate,
            formatTime,
            formatDateShort,
            isDifferenceOutsideThreshold,
            openDispatchLink,
            completeLeg,
            refreshLeg,
            isCompleting,
            isRefreshing,
            compareTimeStrings,
            crewNames,
        };
    },
    components: { LegCheck },
    props: {
        leg: {
            type: Object,
            required: true
        }
    }
}
</script>

<style>
.leg-tag {
    width: 100%;
}

td .button {
    height: 25px;
}

.fms-preview {
    width: 300px;
}
</style>
